import Link from 'next/link'
import { ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'
import { tokenHref } from '../config'
import type { Cad1Tick, ProjectInfoResponse } from '../types'
import { sinceText, tickKindOf, tickOf } from '../verification'
import type { ListVerified } from '../verification'
import { SEAL, SEAL_CHECK, TICK_TONES } from '../art/seal'
import type { TickKind } from '../art/seal'

/*
 * The tick beside a token's name (Bez, 2026-10-04). A twelve-scallop seal, the shape people already read as "verified", lit from
 * the top left like the moon in the hero: blue for the token team's wallet proof, gold for the CADINU team's own review. ONE tick, never both: gold
 * replaces blue (its card says the info is verified too). It scales with the name: 0.8 em of the heading (24 px on a phone, 32 px at
 * 40 px desktop) in a 1.1 em button, never taller than the heading's line, so nothing shifts when it appears. It opens a small card
 * (hover, focus or tap; Escape closes) that says what it means and, for the blue one, where any project gets it. The card is portalled
 * to <body>: the hero clips its overflow and the header sits above the page.
 * The same tick, at about 20 px, sits beside the name on the /tokens cards and in the search rows (ListTick), from the list's `verified`.
 */

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

// the seal's geometry and colours live in art/seal.ts: the server-drawn share card (server/og.tsx) shows the same seal
export type { TickKind }

/** The seal alone (also used inside the cards and the legend), size in px. */
export function Seal({ kind, size = 20 }: { kind: TickKind; size?: number }) {
  const t = TICK_TONES[kind]
  const id = `seal-${kind}-${useId().replace(/:/g, '')}`
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{ flex: 'none', overflow: 'visible', filter: `drop-shadow(0 0 4px ${t.glow}) drop-shadow(0 1px 1px rgba(0,0,0,0.45))` }}>
      <defs>
        <radialGradient id={id} cx="35%" cy="28%" r="80%">
          <stop offset="0%" stopColor={t.hi} />
          <stop offset="48%" stopColor={t.mid} />
          <stop offset="100%" stopColor={t.lo} />
        </radialGradient>
      </defs>
      <path d={SEAL} fill={`url(#${id})`} stroke={t.ring} strokeWidth="0.7" />
      <path d={SEAL_CHECK} fill="none" stroke={t.check} strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const Btn = styled.button<{ $compact?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: inherit;
  width: max(32px, 1.1em);
  height: max(32px, 1.1em);
  padding: 0;
  > svg {
    width: max(24px, 0.8em);
    height: max(24px, 0.8em);
  }
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition: transform 120ms ease, background-color 120ms ease;
  &:hover { background: rgba(223, 233, 255, 0.08); }
  &:active { transform: scale(0.94); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 1px; }
  @media (prefers-reduced-motion: reduce) { transition: none; &:active { transform: none; } }
  /* in a list (cards, search rows): a 20 px seal in a 28 px target whose box counts 22 px in the line, so a name with a tick sits
     exactly where a name without one does */
  ${({ $compact }) =>
    $compact &&
    css`
      width: 28px;
      height: 28px;
      margin: -3px 0;
      border-radius: 8px;
      > svg {
        width: 20px;
        height: 20px;
      }
    `}
`
const Pop = styled.div<{ $kind: TickKind }>`
  position: fixed;
  z-index: 60;
  width: min(320px, calc(100vw - 24px));
  padding: 14px 16px 16px;
  border-radius: 16px;
  color: #e9eefc;
  background: linear-gradient(180deg, #141a36 0%, #0a0e22 100%);
  border: 1px solid ${({ $kind }) => ($kind === 'team' ? 'rgba(244, 185, 58, 0.45)' : $kind === 'cad1' ? 'rgba(31, 157, 85, 0.55)' : 'rgba(47, 138, 245, 0.5)')};
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), 0 18px 40px rgba(0, 0, 0, 0.55), 0 0 32px ${({ $kind }) => ($kind === 'team' ? 'rgba(244, 185, 58, 0.14)' : $kind === 'cad1' ? 'rgba(31, 157, 85, 0.16)' : 'rgba(47, 138, 245, 0.16)')};
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  white-space: normal;
  &:focus { outline: none; }
  h4 { display: flex; align-items: center; gap: 8px; margin: 0 0 6px; font-size: 15px; line-height: 20px; font-weight: 800; color: #fff; }
  p { margin: 0; color: #c3cbe0; }
  p + p { margin-top: 8px; }
`
const CardLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  margin-top: 10px;
  padding: 0 14px;
  border-radius: 12px;
  font-weight: 700;
  color: #050713;
  text-decoration: none;
  background: #7fd4e8;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4);
  &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`

type Opened = 'hover' | 'focus' | 'click' | null

/** a pointer event inside an open tick card (portalled to <body>): a search panel that closes on outside clicks must not close for it */
export const inTickCard = (t: EventTarget | null) => Boolean((t as Element | null)?.closest?.('[data-tick-card]'))

function Tick({ kind, label, title, children, testId, compact }: { kind: TickKind; label: string; title: string; children: ReactNode; testId: string; compact?: boolean }) {
  const [open, setOpen] = useState<Opened>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const btn = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>()
  // focus given back by Escape or by leaving the card must not open the card again
  const quiet = useRef(false)
  const popId = useId()

  const place = useCallback(() => {
    const b = btn.current?.getBoundingClientRect()
    if (!b) return
    const w = Math.min(320, window.innerWidth - 24)
    const left = Math.max(12, Math.min(b.left + b.width / 2 - 28, window.innerWidth - w - 12))
    const below = b.bottom + 8
    const h = pop.current?.offsetHeight ?? 180
    const top = below + h > window.innerHeight - 8 && b.top - h - 8 > 8 ? b.top - h - 8 : below
    setPos({ top, left })
  }, [])
  useIsoLayoutEffect(() => {
    if (!open) return undefined
    place()
    const on = () => place()
    window.addEventListener('scroll', on, true)
    window.addEventListener('resize', on)
    return () => {
      window.removeEventListener('scroll', on, true)
      window.removeEventListener('resize', on)
    }
  }, [open, place])
  // a tap or click anywhere else closes it
  useEffect(() => {
    if (!open) return undefined
    const down = (e: PointerEvent) => {
      const t = e.target as Node
      if (btn.current?.contains(t) || pop.current?.contains(t)) return
      setOpen(null)
    }
    document.addEventListener('pointerdown', down)
    return () => document.removeEventListener('pointerdown', down)
  }, [open])
  useEffect(() => () => clearTimeout(hoverTimer.current), [])

  const close = (refocus: boolean) => {
    setOpen(null)
    if (refocus) {
      quiet.current = true
      btn.current?.focus()
      quiet.current = false
    }
  }
  const hoverIn = () => {
    clearTimeout(hoverTimer.current)
    setOpen((o) => o ?? 'hover')
  }
  const hoverOut = () => {
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => setOpen((o) => (o === 'hover' ? null : o)), 160)
  }

  return (
    <>
      <Btn
        ref={btn}
        type="button"
        aria-label={label}
        aria-expanded={Boolean(open)}
        aria-controls={open ? popId : undefined}
        data-testid={testId}
        $compact={compact}
        onClick={() => setOpen((o) => (o === 'click' ? null : 'click'))}
        onFocus={(e) => {
          if (quiet.current) return
          let keyboard = true
          try {
            keyboard = e.currentTarget.matches(':focus-visible')
          } catch {
            // an engine without :focus-visible: any focus opens it
          }
          if (keyboard) setOpen((o) => o ?? 'focus')
        }}
        onBlur={(e) => {
          if (!pop.current?.contains(e.relatedTarget as Node)) setOpen((o) => (o === 'focus' ? null : o))
        }}
        onPointerEnter={(e) => e.pointerType === 'mouse' && hoverIn()}
        onPointerLeave={(e) => e.pointerType === 'mouse' && hoverOut()}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.preventDefault()
            // only the tick's card closes, not a search panel around the tick
            e.stopPropagation()
            close(true)
          }
          // Tab from the tick goes into its card (it is portalled to the end of the page)
          if (e.key === 'Tab' && !e.shiftKey && open) {
            const link = pop.current?.querySelector<HTMLElement>('a')
            if (link) {
              e.preventDefault()
              setOpen('click')
              link.focus()
            }
          }
        }}
      >
        <Seal kind={kind} />
      </Btn>
      {open && typeof document !== 'undefined' &&
        createPortal(
          <Pop
            ref={pop}
            id={popId}
            role="dialog"
            aria-label={title}
            $kind={kind}
            data-tick-card=""
            data-testid={`${testId}-card`}
            style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
            onPointerEnter={(e) => e.pointerType === 'mouse' && hoverIn()}
            onPointerLeave={(e) => e.pointerType === 'mouse' && hoverOut()}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || (e.key === 'Tab' && !e.shiftKey) || (e.key === 'Tab' && e.shiftKey)) {
                e.preventDefault()
                // React carries the key through the portal to the tick's parents: a search panel must not close with the card
                e.stopPropagation()
                close(true)
              }
            }}
          >
            <h4><Seal kind={kind} size={18} />{title}</h4>
            {children}
          </Pop>,
          document.body,
        )}
    </>
  )
}

const Row = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: none;
`

/** While the project info is still loading: an invisible box of the tick's exact size, so the heading already wraps as it will with a
 * tick and nothing below it moves when the answer comes (a token without info loses the box; that is the rare case). */
export function TickSlot() {
  return (
    <Row data-testid="tp-ticks-pending" aria-hidden="true">
      <Btn as="span" style={{ visibility: 'hidden' }} />
    </Row>
  )
}

/** the ownership-signature line; the list does not carry the method, so there it says "controls" (true either way) */
const ownerLine = (teamVerified?: boolean, method?: 'owner' | 'deployer' | null) =>
  !teamVerified ? null : method === 'deployer' ? 'deployed this token (its ownership is renounced)' : method === 'owner' ? 'is this token’s owner' : 'controls this token'

/**
 * The one tick with its card, wherever a token's name is shown: the token page heading, a /tokens card, a search row. `testId` is the
 * prefix ("tp-tick" on the page: tp-tick-team, tp-tick-team-card, tp-tick-info-link). `fee` and `since` are optional (a list does not
 * carry them); `owner` is the ownership-signature line, null for none.
 */
export function OneTick({ kind, address, fee = null, since = null, owner = null, testId = 'tp-tick', compact }: { kind: TickKind; address: string; fee?: string | null; since?: string | null; owner?: string | null; testId?: string; compact?: boolean }) {
  // Bez, 2026-10-09: the page is free; both ticks are paid steps on approved info. Blue = the wallet proof, gold = a CADINU team review.
  return kind === 'info' ? (
    <Tick kind="info" testId={`${testId}-info`} title="Verified token team" label="Verified token team: what this means" compact={compact}>
      <p data-testid={owner ? 'tp-team-verified' : undefined}>
        {owner ? `The wallet that controls this token proved it: it ${owner}` : 'The wallet that controls this token (its owner, or its deployer when ownership is renounced) proved it'}
        {since ? `, on ${since}` : ''}.
      </p>
      <p>It shows who controls the token. It is not an audit and says nothing about the project&apos;s quality.</p>
      <p>Any token team can get it{fee ? ` (${fee === 'Free' ? 'free' : fee})` : ''}.</p>
      <Link href={`${tokenHref(address)}#ticks`} passHref legacyBehavior>
        <CardLink data-testid={`${testId}-info-link`}>How to get the ticks</CardLink>
      </Link>
    </Tick>
  ) : (
    <Tick kind="team" testId={`${testId}-team`} title="Verified by CADINU team" label="Verified by CADINU team: what this means" compact={compact}>
      <p>The CADINU team reviewed this project itself{since ? `, on ${since}` : ''}.</p>
      <p data-testid={`${testId}-team-info`}>A review, not an audit or a guarantee. Its project info was also reviewed by a person before it went live.</p>
      {owner && <p data-testid="tp-team-verified">The wallet that controls the token also proved it: it {owner}.</p>}
      <p>A token team can ask for this review on its token page.</p>
    </Tick>
  )
}

/**
 * The one tick a token has earned: gold when the CADINU team vetted it, else blue for approved info. Nothing when it has neither (the
 * header's missing slots carry the way in). `fee` is the blue tick's fee as the API's admin setting states it ("0.01 BNB", or "Free").
 */
export default function VerifyTicks({ address, info, fee }: { address: string; info?: ProjectInfoResponse | null; fee: string | null }) {
  const one = tickOf(info)
  if (!one) return null
  return (
    <Row data-testid="tp-ticks" data-tick={one}>
      <OneTick kind={one} address={address} fee={fee} since={sinceText(one === 'team' ? info?.cadinuVerifiedAt : info?.blueVerifiedAt)} owner={ownerLine(info?.teamVerified, info?.teamVerifiedMethod === 'deployer' ? 'deployer' : 'owner')} />
    </Row>
  )
}

const Lifted = styled(Row)`
  /* above the card's or the row's stretched link, so the tick takes its own hover, focus and tap */
  position: relative;
  z-index: 1;
`

/**
 * The same one tick on a /tokens card and in a search row (Bez, 2026-10-04), from the list's `verified` (one token_pages read per list
 * build, no request per card): about 20 px, its box no taller than the name's line, the same card on hover, focus or tap. Nothing for
 * null. `testId` is the prefix: "tp-card-tick" gives tp-card-ticks (data-tick team | info), tp-card-tick-team, tp-card-tick-team-card.
 */
export function ListTick({ address, verified, teamVerified, testId }: { address: string; verified?: ListVerified; teamVerified?: boolean; testId: string }) {
  const kind = tickKindOf(verified)
  if (!kind) return null
  return (
    <Lifted data-testid={`${testId}s`} data-tick={kind}>
      <OneTick kind={kind} address={address} owner={ownerLine(teamVerified)} testId={testId} compact />
    </Lifted>
  )
}

/** "6 Oct 2036" for a unix time in seconds (UTC); null when there is none */
export const lockDateText = (unix: number | null | undefined): string | null =>
  typeof unix === 'number' && Number.isFinite(unix) && unix > 0 ? new Date(unix * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : null

/**
 * The green "Launched with CAD1" tick (Bez, 2026-10-09/10), beside the blue or gold one (it is a different fact, so both can show): automatic
 * and free for every token in a CAD1 launcher registry, read by the server (the overview's / list's `cad1`). Its card shows the launch lock
 * ("LP locked until <date>") and links the lock certificate. It is not a review: the blue and gold ticks stay optional and paid.
 */
export function Cad1LaunchTick({ cad1, testId = 'tp-cad1-tick', compact }: { address?: string; cad1: Cad1Tick; testId?: string; compact?: boolean }) {
  const until = lockDateText(cad1.lockUntil)
  const launched = lockDateText(cad1.launch.at)
  return (
    <Tick kind="cad1" testId={testId} title="Launched with CAD1" label={`Launched with CAD1${until ? `, LP locked until ${until}` : ''}: what this means`} compact={compact}>
      <p data-testid={`${testId}-lock`}>{until ? `LP locked until ${until}.` : 'Its pool’s liquidity was locked at launch.'}</p>
      <p>Made by the CAD1 launcher{launched ? ` on ${launched}` : ''}, its pool and lock in the same transaction. Automatic for every CAD1 launch: not a review or an audit.</p>
      {cad1.launch.lockId ? (
        <Link href={`/lock/v3/${cad1.launch.lockId}`} passHref legacyBehavior>
          <CardLink data-testid={`${testId}-certificate`}>Lock certificate</CardLink>
        </Link>
      ) : null}
    </Tick>
  )
}

/** The same green tick on a /tokens card, a search row and the swap token picker: 20 px, above the row's own link. Nothing for null. */
export function ListCad1Tick({ cad1, testId }: { cad1?: Cad1Tick | null; testId: string }) {
  if (!cad1) return null
  return (
    <Lifted data-testid={`${testId}s`} data-tick="cad1">
      <Cad1LaunchTick cad1={cad1} testId={testId} compact />
    </Lifted>
  )
}
